import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Layout e Componentes do Sistema
import { PopupProvider } from '../providers/PopupProvider';
import { ToastProvider } from '../providers/ToastProvider';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SessionTimeout from '../components/SessionTimeout';
import EnvironmentBanner from '../components/EnvBanner';
import Loading from '../components/Loading';

// Utilitários, Segurança e Motores de Sessão
import { TokenUtil } from '../utils/Utils';
import { useAuth } from "../hooks/useAuth";
import { UserController } from "../../mtlogin-api/controllers/MTLoginUserController";
import { Environment } from "../../shared-api/helpers/Environment";

// Regras de Negócio da Remuneração
import { RemuneracaoService, IRemuneracaoListagem } from '../../fe-api/services/RemuneracaoService';
import { TipoVerba } from '../../constantes/Dominios';

const remuneracaoService = new RemuneracaoService();

export const RemuneracaoCrudPage: React.FC = () => {
    const navigate = useNavigate();
    const { session, login } = useAuth();
    const { token } = useParams<{ token: string }>();
    
    const reidratando = useRef(false);
    const fileInputRef = useRef<HTMLInputElement>(null); 
    const [erroConfig, setErroConfig] = useState('');

    // Estados dos formulários do CRUD
    const [file, setFile] = useState<File | null>(null);
    const [competencia, setCompetencia] = useState('');
    const [tipoVerba, setTipoVerba] = useState<TipoVerba>(1);
    const [statusMensagem, setStatusMensagem] = useState('');
    
    // NOVO: Estado para armazenar os registros carregados do Banco
    const [listaArquivos, setListaArquivos] = useState<IRemuneracaoListagem[]>([]);

    useEffect(() => {
        const inicializarErestaurarSessao = async () => {
            if (token) {
                TokenUtil.setToken(token);
            }

            if (!session?.loggedIn && !reidratando.current) {
                reidratando.current = true;
                const tokenSalvo = TokenUtil.getToken();

                if (tokenSalvo) {
                    try {
                        const userController = new UserController();
                        const sessionData = await userController.getUserInformation();

                        if (sessionData && sessionData.loggedIn) {
                            login(sessionData.loggedIn, sessionData.registered);
                        } else {
                            navigate('/logininitiator');
                        }
                    } catch (error) {
                        console.error("Erro ao validar credenciais do Portal:", error);
                        setErroConfig('Sua sessão não pôde ser validada junto ao Portal da Transparência.');
                    }
                } else {
                    setErroConfig('Acesso negado: Token de autenticação não fornecido.');
                }
            }
        };

        inicializarErestaurarSessao();
    }, [session, login, navigate, token]);

    const timeout = Number(Environment.get('REACT_APP_TIMEOUT_IN_MINUTES', '10'));
    
    const handleLogout = useCallback(async () => {
        navigate('/signout');
    }, [navigate]);

    // Mapeamento estático para exibição dos nomes textuais das verbas
    const labelsVerba: Record<number, string> = {
        1: "Subsídio",
        2: "Comissionada",
        3: "Indenizações Eventuais",
        4: "Indenizações Mensais"
    };

    // --- FUNÇÃO AUXILIAR DE VALIDAÇÃO DE FORMATO (MM/YYYY) ---
    const validarFormatoCompetencia = (valor: string): boolean => {
        const regex = /^(0[1-9]|1[0-2])\/\d{4}$/;
        return regex.test(valor);
    };

    const formatarDataIsoParaCompetencia = (dataIso: string): string => {
        if (!dataIso) return "";
        const parts = dataIso.split("-"); // Quebra "2026-05-01"
        if (parts.length < 2) return dataIso;
        return `${parts[1]}/${parts[0]}`; // Retorna "05/2026"
    };

    // --- NOVO: VALIDAÇÃO DE MÊS RETRASADO ---
    const validarMesRetrasado = (valor: string): boolean => {
        const [mes, ano] = valor.split('/').map(Number);
        const dataCompetencia = new Date(ano, mes - 1, 1);
        
        const hoje = new Date();
        // Primeiro dia do mês atual
        const inicioMesAtual = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        
        // Calcula a diferença em meses
        const diferencaMeses = (inicioMesAtual.getFullYear() - dataCompetencia.getFullYear()) * 12 + (inicioMesAtual.getMonth() - dataCompetencia.getMonth());
        
        // Só permite se a diferença for de 2 meses ou mais (retrasado)
        return diferencaMeses >= 2;
    };

    // Formata o texto em tempo de execução inserindo a barra automaticamente
    const handleTextFormatFix = (e: React.ChangeEvent<HTMLInputElement>) => {
        let valor = e.target.value.replace(/\D/g, ""); 
        if (valor.length > 2) {
            valor = `${valor.substring(0, 2)}/${valor.substring(2, 6)}`; 
        }
        setCompetencia(valor);
    };

    // --- NOVO: FUNÇÃO DE CONSULTA (LISTAR TODOS) ---
    const handleListar = async () => {
        try {
            setStatusMensagem('Buscando registros no banco de dados...');
            const dados = await remuneracaoService.listarArquivos();
            setListaArquivos(dados);
            setStatusMensagem(`Consulta concluída! ${dados.length} registros encontrados.`);
        } catch (err: any) {
            setStatusMensagem(`Erro ao listar arquivos: ${err.message}`);
        }
    };

    // --- FUNÇÕES OPERACIONAIS DO CRUD ---
    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file || !competencia) return alert('Selecione o arquivo PDF e preencha a competência!');
        
        if (!validarFormatoCompetencia(competencia)) {
            setStatusMensagem('Erro: A competência deve estar estritamente no formato MM/YYYY (ex: 04/2026).');
            return;
        }

        try {
            setStatusMensagem('Enviando documento...');
            await remuneracaoService.uploadArquivo(file, competencia, tipoVerba);
            setStatusMensagem('Documento inserido com sucesso!');
            handleListar(); // Atualiza a tabela após criar
        } catch (err: any) {
            setStatusMensagem(`Erro no Upload: ${err.message}`);
        }
    };

    const handleAtualizar = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!file || !competencia) return alert('Selecione o arquivo PDF e preencha a competência!');
        
        if (!validarFormatoCompetencia(competencia)) {
            setStatusMensagem('Erro: A competência deve estar estritamente no formato MM/YYYY (ex: 04/2026).');
            return;
        }

        try {
            setStatusMensagem('Substituindo documento existente...');
            await remuneracaoService.atualizarArquivo(file, competencia, tipoVerba);
            setStatusMensagem('Documento atualizado com sucesso!');
            handleListar(); // Atualiza a lista após o PUT
        } catch (err: any) {
            setStatusMensagem(`Erro na Atualização: ${err.message}`);
        }
    };

    const handleDelete = async () => {
        if (!competencia) return alert('Informe a competência (MM/YYYY) que deseja excluir.');
        
        if (!validarFormatoCompetencia(competencia)) {
            setStatusMensagem('Erro: A competência deve estar no formato MM/YYYY.');
            return;
        }

        
        if (!validarMesRetrasado(competencia)) {
            setStatusMensagem('Erro: Operação negada. Só é permitido excluir arquivos do mês retrasado para trás.');
            alert('Erro: Só é possível excluir arquivos do mês retrasado!');
            return;
        }

        const verbaNome = labelsVerba[tipoVerba] || "";

        const confirmado = window.confirm(
            `ATENÇÃO: Deseja realmente excluir o arquivo de remuneração da competência [ ${competencia} ] e tipo de verba [ ${verbaNome} ]?`
        );

        if (!confirmado) {
            setStatusMensagem('Exclusão cancelada pelo usuário.');
            return; 
        }

        try {
            setStatusMensagem('Removendo registro do banco de dados...');
            await remuneracaoService.deletarArquivo(competencia, tipoVerba);
            setStatusMensagem(`Registro da competência [${competencia}] excluído com sucesso.`);
            handleListar(); // Atualiza a lista após o DELETE
        } catch (err: any) {
            setStatusMensagem(`Erro ao excluir: ${err.message}`);
        }
    };

    // NOVO: Ação rápida executada ao clicar em "Atualizar" direto em uma linha da tabela
    const executarAcaoLinha = (comp: string, verba: number, acao: 'PUT' | 'DELETE') => {
        setCompetencia(comp);
        setTipoVerba(verba as TipoVerba);

        if (acao === 'PUT') {
            setStatusMensagem(`Campos carregados! Agora selecione o novo arquivo PDF acima e clique no botão 'Substituir (PUT)'.`);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (acao === 'DELETE') {
            // Usa o setTimeout pequeno para garantir que o React atualizou os states acima antes de invocar o confirm
            setTimeout(() => {
                handleDelete();
            }, 100);
        }
    };

    if (erroConfig) {
        return <div style={{ color: 'red', textAlign: 'center', padding: '50px', fontWeight: 'bold' }}>{erroConfig}</div>;
    }

    if (!session?.loggedIn) {
        return <Loading />;
    }

    return (
        <>
            <SessionTimeout timeInMinutes={timeout} action={handleLogout} />
            <EnvironmentBanner />
            
            <PopupProvider>
                <ToastProvider>
                    <Navbar onLogout={handleLogout} />
                    
                    <div style={{ maxWidth: '1200px', margin: '30px auto', padding: '0 20px', fontFamily: 'var(--font-family, sans-serif)' }}>
                        <div style={{ backgroundColor: '#fff', borderRadius: '6px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '24px' }}>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #eaeaea', paddingBottom: '12px', marginBottom: '20px' }}>
                                <h2 style={{ margin: 0, color: '#333', fontSize: '1.5rem' }}>
                                    <i className="pi pi-cog mr-2" style={{ color: '#0056b3' }}></i> 
                                    Configurações e Carga de Arquivos de Remuneração
                                </h2>
                                {statusMensagem && (
                                    <span style={{ backgroundColor: statusMensagem.startsWith('Erro') ? '#ffebee' : '#e1f5fe', color: statusMensagem.startsWith('Erro') ? '#c62828' : '#0288d1', padding: '6px 12px', borderRadius: '4px', fontSize: '0.9rem', fontWeight: 'bold' }}>
                                        {statusMensagem}
                                    </span>
                                )}
                            </div>

                            {/* PAINEL CENTRALIZADO DE GERENCIAMENTO */}
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '4px', padding: '20px', backgroundColor: '#fafafa', marginBottom: '30px' }}>
                                <h3 style={{ marginTop: 0, marginBottom: '4px', fontSize: '1.1rem', color: '#555' }}>
                                    <i className="pi pi-sliders-h mr-2"></i> Painel de Controle Integrado (CUD)
                                </h3>
                                
                                <p style={{ color: '#cc2525', fontSize: '0.82rem', margin: '0 0 18px 0', fontWeight: '500' }}>
                                    <i className="pi pi-exclamation-triangle mr-1"></i> Atenção: Só é possivel excluir arquivos do mes retrazado.
                                </p>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '25px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label style={{ fontWeight: '600', marginBottom: '5px', fontSize: '0.85rem' }}>Competência * (MM/YYYY)</label>
                                        <input 
                                            type="text" 
                                            placeholder="Ex: 04/2026" 
                                            maxLength={7}
                                            value={competencia} 
                                            onChange={handleTextFormatFix} 
                                            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} 
                                        />
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label style={{ fontWeight: '600', marginBottom: '5px', fontSize: '0.85rem' }}>Tipo de Verba *</label>
                                        <select value={tipoVerba} onChange={(e) => setTipoVerba(Number(e.target.value) as TipoVerba)} style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fff' }}>
                                            <option value="1">Subsídio</option>
                                            <option value="2">Comissionada</option>
                                            <option value="3">Indenizações Eventuais</option>
                                            <option value="4">Indenizações Mensais</option>
                                        </select>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label style={{ fontWeight: '600', marginBottom: '5px', fontSize: '0.85rem' }}>Arquivo Binário (PDF)</label>
                                        <input 
                                            type="file" 
                                            accept="application/pdf" 
                                            ref={fileInputRef} 
                                            onChange={(e) => setFile(e.target.files?.[0] || null)} 
                                            style={{ fontSize: '0.85rem' }} 
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    <button onClick={handleUpload} style={{ padding: '10px 16px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                                        <i className="pi pi-plus mr-2"></i> Inserir Novo (POST)
                                    </button>
                                    <button onClick={handleAtualizar} style={{ padding: '10px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                                        <i className="pi pi-refresh mr-2"></i> Substituir (PUT)
                                    </button>
                                    <button onClick={handleDelete} style={{ padding: '10px 16px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                                        <i className="pi pi-trash mr-2"></i> Excluir Registro (DELETE)
                                    </button>
                                    
                                    {/* NOVO BOTÃO: CONSULTAR E CARREGAR TABELA */}
                                    <button onClick={handleListar} style={{ padding: '10px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', marginLeft: 'auto' }}>
                                        <i className="pi pi-search mr-2"></i> Consultar Registros (GET)
                                    </button>
                                </div>
                            </div>

                            {/* NOVO PAINEL: LISTAGEM EM TABELA (SÓ RENDERIZA SE TIVER DADOS) */}
                            {listaArquivos.length > 0 && (
                                <div style={{ border: '1px solid #e0e0e0', borderRadius: '4px', padding: '20px', backgroundColor: '#fff' }}>
                                    <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '1.1rem', color: '#333' }}>
                                        <i className="pi pi-list mr-2" style={{ color: '#3b82f6' }}></i> Arquivos Gravados no Banco de Dados
                                    </h3>
                                    
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                                                    <th style={{ padding: '12px 8px', fontWeight: '600' }}>ID</th>
                                                    <th style={{ padding: '12px 8px', fontWeight: '600' }}>Competência</th>
                                                    <th style={{ padding: '12px 8px', fontWeight: '600' }}>Tipo de Verba</th>
                                                    <th style={{ padding: '12px 8px', fontWeight: '600' }}>Data de Inclusão</th>
                                                    <th style={{ padding: '12px 8px', fontWeight: '600' }}>ID Operador</th>
                                                    <th style={{ padding: '12px 8px', fontWeight: '600', textAlign: 'center' }}>Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {listaArquivos.map((arq) => (
                                                    <tr key={arq.id} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: competencia === formatarDataIsoParaCompetencia(arq.dataCompetencia) && tipoVerba === arq.tipoVerba ? '#fffbeb' : 'transparent' }}>
                                                        <td style={{ padding: '10px 8px', color: '#64748b' }}>{arq.id}</td>
                                                        
                                                        {/* CORRIGIDO: Agora converte o "YYYY-MM-DD" para "MM/YYYY" na tela */}
                                                        <td style={{ padding: '10px 8px', fontWeight: 'bold' }}>{formatarDataIsoParaCompetencia(arq.dataCompetencia)}</td>
                                                        
                                                        <td style={{ padding: '10px 8px' }}>{labelsVerba[arq.tipoVerba] || arq.tipoVerba}</td>
                                                        <td style={{ padding: '10px 8px', color: '#64748b' }}>{new Date(arq.dataInclusao).toLocaleString('pt-BR')}</td>
                                                        <td style={{ padding: '10px 8px', color: '#64748b' }}>{arq.usuarioAutenticadoID}</td>
                                                        <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                                            
                                                            <button 
                                                                onClick={() => executarAcaoLinha(formatarDataIsoParaCompetencia(arq.dataCompetencia), arq.tipoVerba, 'PUT')}
                                                                title="Selecionar esta linha para atualizar o PDF"
                                                                style={{ padding: '6px 10px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '6px', fontSize: '0.8rem' }}
                                                            >
                                                                <i className="pi pi-refresh"></i> Atualizar
                                                            </button>

                                                            <button 
                                                                onClick={() => executarAcaoLinha(formatarDataIsoParaCompetencia(arq.dataCompetencia), arq.tipoVerba, 'DELETE')}
                                                                title="Excluir este registro"
                                                                style={{ padding: '6px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
                                                            >
                                                                <i className="pi pi-trash"></i> Excluir
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>

                </ToastProvider>
            </PopupProvider>
            
            <Footer />
        </>
    );
};